import { IonModal } from '@ionic/react';
import { useRef } from 'react';

import { useIosInstallHint } from '~/hooks/useIosInstallHint';

interface IosInstallSheetProps {
  isOpen: boolean;
  onDismiss(mode: 'remind-later' | 'installed'): void;
}

/**
 * Bottom sheet com instruções pra instalar o PWA no iPhone/iPad.
 * Usa IonModal em modo drawer (breakpoints) — visual já familiar pro iOS.
 *
 * O conteúdo é estático (não consulta nenhuma API), então o sheet pode
 * abrir antes mesmo do auth resolver — fica preso ao componente que o
 * monta, sem dependências cruzadas.
 */
export function IosInstallSheet({ isOpen, onDismiss }: IosInstallSheetProps): JSX.Element {
  const modalRef = useRef<HTMLIonModalElement>(null);

  const close = (mode: 'remind-later' | 'installed'): void => {
    onDismiss(mode);
    void modalRef.current?.dismiss();
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      breakpoints={[0, 1]}
      initialBreakpoint={1}
      handle
      onDidDismiss={() => onDismiss('remind-later')}
      style={
        {
          '--height': 'auto',
          '--border-radius': '20px 20px 0 0',
          '--background': 'var(--rx-surface)',
        } as React.CSSProperties
      }
    >
      <div
        style={{
          padding: '4px 24px 28px',
          color: 'var(--rx-text)',
          fontFamily: 'Geist, ui-sans-serif, sans-serif',
        }}
      >
        <header style={{ paddingTop: 8 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--rx-brand-tint)',
              color: 'var(--rx-brand)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.2px',
              textTransform: 'uppercase',
            }}
          >
            <DotIcon />
            iPhone · iPad
          </div>
          <h2
            style={{
              margin: '14px 0 6px',
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: '-0.4px',
              lineHeight: 1.15,
            }}
          >
            Instale o app pra receber notificações
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--rx-text-2)',
              lineHeight: 1.45,
            }}
          >
            Sem instalar na tela inicial, o iOS bloqueia push de qualquer site. Tres passos no
            Safari resolvem.
          </p>
        </header>

        <ol
          style={{
            listStyle: 'none',
            margin: '20px 0 0',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <Step number={1}>
            Toque em <Inline label="Compartilhar" icon={<ShareIcon />} /> no menu inferior da barra
            do Safari.
          </Step>
          <Step number={2}>
            Role a lista e escolha{' '}
            <Inline label="Adicionar à Tela de Início" icon={<PlusBoxIcon />} />.
          </Step>
          <Step number={3}>
            Confirme em <Inline label="Adicionar" /> no canto superior direito.
          </Step>
        </ol>

        <div
          style={{
            marginTop: 18,
            padding: '10px 14px',
            background: 'var(--rx-surface-alt)',
            borderRadius: 12,
            fontSize: 12,
            color: 'var(--rx-text-2)',
            lineHeight: 1.45,
          }}
        >
          Depois de instalado, abra o ícone novo da tela inicial e o toggle de notificações em{' '}
          <strong style={{ color: 'var(--rx-text)' }}>Perfil</strong> fica disponível.
        </div>

        <div
          style={{
            marginTop: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => close('installed')}
            style={{
              padding: '14px 16px',
              background: 'var(--rx-brand)',
              color: 'var(--rx-brand-ink)',
              border: 'none',
              borderRadius: 14,
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
              letterSpacing: '-0.1px',
            }}
          >
            Já instalei
          </button>
          <button
            type="button"
            onClick={() => close('remind-later')}
            style={{
              padding: '12px 16px',
              background: 'transparent',
              color: 'var(--rx-text-2)',
              border: 'none',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Lembrar depois
          </button>
        </div>
      </div>
    </IonModal>
  );
}

/**
 * Variante "controlada por auto-detecção" — usa o hook diretamente.
 * Pra montar no Dashboard sem prop-drilling de estado.
 */
export function IosInstallSheetAuto(): JSX.Element | null {
  const hint = useIosInstallHint();
  if (!hint.shouldAutoShow) return null;
  return <IosInstallSheet isOpen onDismiss={hint.dismiss} />;
}

// ─────────────────────────────────────────────────────────────────────────────

function Step({ number, children }: { number: number; children: React.ReactNode }): JSX.Element {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--rx-surface)',
        border: '0.5px solid var(--rx-border)',
        borderRadius: 12,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 26,
          height: 26,
          borderRadius: 8,
          background: 'var(--rx-brand-tint)',
          color: 'var(--rx-brand)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '-0.1px',
        }}
      >
        {number}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 14,
          color: 'var(--rx-text)',
          lineHeight: 1.5,
        }}
      >
        {children}
      </span>
    </li>
  );
}

function Inline({ label, icon }: { label: string; icon?: React.ReactNode }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        margin: '0 2px',
        borderRadius: 6,
        background: 'var(--rx-surface-alt)',
        color: 'var(--rx-text)',
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {icon && <span style={{ display: 'inline-flex' }}>{icon}</span>}
      {label}
    </span>
  );
}

function DotIcon(): JSX.Element {
  return (
    <svg width="6" height="6" viewBox="0 0 6 6" role="img" aria-label="marcador">
      <title>marcador</title>
      <circle cx="3" cy="3" r="3" fill="currentColor" />
    </svg>
  );
}

/** Ícone do botão Compartilhar do iOS — quadrado com seta saindo. */
function ShareIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="ícone Compartilhar"
    >
      <title>Compartilhar</title>
      <path d="M12 3v13" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 14v6h14v-6" />
    </svg>
  );
}

/** Ícone "Adicionar à Tela de Início" — caixa com + central. */
function PlusBoxIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="ícone Adicionar à Tela de Início"
    >
      <title>Adicionar à Tela de Início</title>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}
